import { Quote } from "types/quotes";

// Em Agendamento (5) + Concluído (6) = transacionado.
export const TRANSACTED_STATUSES: number[] = [5, 6];

export interface RedeTransaction {
  id: number;
  professionalId: number;
  patientName: string;
  professionalName: string;
  date: string;
  transacionado: number;
  repassado: number;
  retido: number;
  status: number;
}

export interface ProfessionalAggregate {
  id: number;
  name: string;
  count: number;
  bruto: number;
  repasse: number;
  retido: number;
  effectiveSplitDoctorPct: number;
  effectiveSplitPlatformPct: number;
  hasData: boolean;
  // Transações desse profissional, para o detalhamento do split (data desc).
  transactions: RedeTransaction[];
}

export interface AggregateResult {
  perProfessional: ProfessionalAggregate[];
  transactions: RedeTransaction[];
}

export function aggregateRede(
  quotes: Quote[],
  professionals: { id: number; name: string }[]
): AggregateResult {
  const map = new Map<
    number,
    {
      id: number;
      name: string;
      count: number;
      bruto: number;
      repasse: number;
      transactions: RedeTransaction[];
    }
  >();

  // Semeia todos os médicos da rede (geradores e não-geradores).
  professionals.forEach((p) =>
    map.set(p.id, {
      id: p.id,
      name: p.name,
      count: 0,
      bruto: 0,
      repasse: 0,
      transactions: [],
    })
  );

  const transactions: RedeTransaction[] = [];

  quotes.forEach((quote) => {
    if (!TRANSACTED_STATUSES.includes(quote.quoteStatus)) return;

    const profId = quote.professional?.id ?? -1;
    const profName = quote.professional?.name ?? "—";
    const transacionado = quote.payment?.value ?? 0;
    const repassado = quote.payment?.compensationValue ?? 0;

    const tx: RedeTransaction = {
      id: quote.id,
      professionalId: profId,
      patientName: quote.patient?.name ?? "—",
      professionalName: profName,
      date: quote.creationDate,
      transacionado,
      repassado,
      retido: transacionado - repassado,
      status: quote.quoteStatus,
    };

    const entry =
      map.get(profId) ??
      { id: profId, name: profName, count: 0, bruto: 0, repasse: 0, transactions: [] };
    entry.count += 1;
    entry.bruto += transacionado;
    entry.repasse += repassado;
    entry.transactions.push(tx);
    map.set(profId, entry);

    transactions.push(tx);
  });

  const perProfessional: ProfessionalAggregate[] = Array.from(map.values())
    .map((e) => {
      const hasData = e.bruto > 0;
      const doctorPct = hasData ? e.repasse / e.bruto : 0;
      return {
        id: e.id,
        name: e.name,
        count: e.count,
        bruto: e.bruto,
        repasse: e.repasse,
        retido: e.bruto - e.repasse,
        effectiveSplitDoctorPct: doctorPct,
        effectiveSplitPlatformPct: hasData ? 1 - doctorPct : 0,
        hasData,
        transactions: [...e.transactions].sort((a, b) =>
          a.date < b.date ? 1 : -1
        ),
      };
    })
    .sort((a, b) => b.bruto - a.bruto);

  transactions.sort((a, b) => (a.date < b.date ? 1 : -1));

  return { perProfessional, transactions };
}
